import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar, FileText, Check } from "lucide-react";
import { ApiService } from "../../services/api";
import { CreateMeetingData } from "../../types/meeting";
import ParticipantManager from "./components/ParticipantManager";
import { useModal } from "../../hooks/useModal";

export default function NewMeeting() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useModal();
  const [loading, setLoading] = useState(false);
  const [meetingId, setMeetingId] = useState<string | null>(null);

  const pad = (n: number) => String(n).padStart(2, "0");
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getDefaultEndDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState<CreateMeetingData>({
    title: "",
    description: "",
    startDate: getCurrentDateTime(),
    endDate: getDefaultEndDateTime(),
    referenceId: "",
    referenceType: "",
    metadata: {},
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700";

  const EXAMPLE_PREFILL_KEY = "meetingExamplePrefill";
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(EXAMPLE_PREFILL_KEY);
      if (cached) {
        const data = JSON.parse(cached) as {
          title?: string;
          description?: string;
        };

        const fromList = Boolean((location as any)?.state?.fromList);
        if (fromList) {
          setFormData((prev) => ({
            ...prev,
            title: data.title || prev.title,
            description: data.description || prev.description,
          }));
        }
      }
    } catch {}
  }, [location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "startDate" || name === "endDate") {
      let corrected = value;
      if (value) {
        const [datePart] = value.split("T");
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        if (datePart === todayStr) {
          const chosen = new Date(value);
          const floorNow = new Date(`${todayStr}T${pad(now.getHours())}:${pad(now.getMinutes())}`);
          if (chosen.getTime() < floorNow.getTime()) {
            corrected = `${todayStr}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
          }
        }
      }
      setFormData((prev) => ({ ...prev, [name]: corrected }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Toplantı başlığı gereklidir";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Toplantı açıklaması gereklidir";
    }

    if (!formData.startDate) newErrors.startDate = "Başlangıç tarihi gereklidir";
    if (!formData.endDate) newErrors.endDate = "Bitiş tarihi gereklidir";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),

        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        metadata: {},
      };

      const created = await ApiService.createMeeting(submitData);
      let newMeetId = created?.id;
      if (!newMeetId && created?.data) {
        newMeetId = created.data.id || created.data.meetId;
      }

      if (!newMeetId) {
        throw new Error("Oluşturulan toplantı ID'si alınamadı");
      }

      setMeetingId(newMeetId);
      try {
        const t = formData.title?.trim();
        const d = formData.description?.trim();
        if (t || d) {
          sessionStorage.setItem(EXAMPLE_PREFILL_KEY, JSON.stringify({ title: t || "", description: d || "" }));
        }
      } catch {}
      localStorage.setItem("meetingListShouldRefresh", "true");
    } catch (error: any) {
      console.error("Toplantı oluşturma hatası:", error);
      await showAlert({
        title: "Hata",
        message: error.message || "Toplantı oluşturulurken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-hidden flex flex-col">
          <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
            <div className="px-6 py-3">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Toplantılar</span>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <button
                    onClick={() => navigate("/meetings")}
                    className="pb-3 transition-all duration-200 text-emerald-600 font-medium border-b-2 border-emerald-500">
                    Tüm Toplantılar
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Yeni Toplantı</h1>
                    <p className="text-sm text-gray-600 mt-1">Toplantı bilgilerini doldurun ve katılımcıları ekleyin.</p>
                    <div className="mt-3 h-1 w-20 rounded-full bg-emerald-200" />
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/meetings")}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50">
                    Geri Dön
                  </button>
                </div>

                {meetingId && (
                  <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-green-700 text-sm">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">Toplantı Başarıyla Oluşturuldu</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Temel Bilgiler</span>
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Toplantı Başlığı *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`${fieldClassName} ${
                        errors.title ? "border-red-300 bg-red-50" : ""
                      }`}
                      placeholder="Toplantı başlığını buraya yazın... (Örn: Haftalık Durum Toplantısı)"
                      required
                      disabled={Boolean(meetingId)}
                    />
                    {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                  </div>
                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Açıklama</span>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className={`${fieldClassName} ${
                        errors.description ? "border-red-300 bg-red-50" : ""
                      }`}
                      placeholder="Toplantının amacını ve gündemini burada açıklayın..."
                      disabled={Boolean(meetingId)}
                    />
                    {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Tarih ve Saat</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi ve Saati *</label>
                      <input
                        type="datetime-local"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        min={getCurrentDateTime()}
                        className={`${fieldClassName} ${
                          errors.startDate ? "border-red-300 bg-red-50" : ""
                        }`}
                        disabled={Boolean(meetingId)}
                      />
                      {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
                      </div>

                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi ve Saati *</label>
                      <input
                        type="datetime-local"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        min={getCurrentDateTime()}
                        className={`${fieldClassName} ${
                          errors.endDate ? "border-red-300 bg-red-50" : ""
                        }`}
                        disabled={Boolean(meetingId)}
                      />
                      {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
                      </div>
                    </div>
                  </div>

                  {!meetingId && (
                    <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => navigate("/meetings")}
                          className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                          İptal
                        </button>
                        <button
                          type="submit"
                          disabled={!formData.title.trim() || loading}
                          className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-md hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60 text-sm">
                          {loading ? "Oluşturuluyor..." : "Toplantı Oluştur"}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {meetingId && (
                <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Katılımcı Yönetimi</h3>
                  </div>
                  <ParticipantManager meetingId={meetingId} onComplete={() => navigate("/meetings")} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
